/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import axios from "axios";
import { config } from "../../../../Helpers/env";
import * as snackbarActions from "../../../../actions/snackbarActions";
import PageTitle from "../../../Utils/smsTitle";
import { SubHeader } from "../../../../Containers/SubHeader";
import MainSnackbar from "../../../Utils/Snackbar/SmsSnackbar";
import ObGynSidebar from "../../Utils/ObGynSidebar";
import ObGynPatientInfo from "../../Utils/ObGynPatientInfo";
import { useDeliveryRecord } from "./hooks/useDeliveryRecord";
import { useNewbornRecord } from "../NewbornRecord/hooks/useNewbornRecord";
import { DeliveryRecordForm } from "./DeliveryRecordForm";
import { NewbornAssessmentTab } from "../NewbornRecord/NewbornAssessmentTab";
import { history } from "../../../../Helpers/history";
import {
    DRPageWrapper, DRHeader, DRHeaderLeft, DRHeaderTitle, DRHeaderSub,
    DRHeaderStats, DRStatChip, DRStatusBadge,

    DRAlertContainer, DRAlertCard, DRAlertIcon, DRAlertContent,
    DRAlertTitle, DRAlertDetail, DRAlertAction,
    DRSummaryStrip, DRSummaryItem, DRSummaryLabel, DRSummaryValue,
    DRLoading, DRNoAdmission, DRNoAdmissionIcon, DRNoAdmissionTitle,
    DRNoAdmissionDesc, DRPrimaryBtn, DRSecondaryBtn, DRAdmissionBtn,
    DRRecordCard, DRRecordRow, DRRecordLabel, DRRecordValue,
    DRButtonRow,
} from "./DeliveryRecordStyles";
import { ObGynTabBar, ObGynTabBtn } from "../../Utils/ObGynTabStyles";

const DeliveryRecordPage = function(props) {
    const actions = props.actions;
    const snackbar = props.snackbar;
    const params = props.match.params;
    const patient_number = params.patient_number;
    const visit_id = params.visit_id;
    const patient_type = params.patient_type;

    const user = JSON.parse(sessionStorage.getItem("user"));
    const userObj = !user ? {} : user;
    const token = !userObj.token ? [] : userObj.token;
    const _token = !token[1] ? {} : token[1];
    const doctor_name = (!_token.first_name ? "" : _token.first_name) + " " +
        (!_token.last_name ? "" : _token.last_name);

    /* ── Hooks ── */
    const dr = useDeliveryRecord(actions, patient_number, visit_id);
    const nb = useNewbornRecord(actions, patient_number, visit_id, function() {
        fetchPairs();
        dr.setActiveTab("summary");
    });

    /* ── Sync delivery record into newborn hook after save ── */
    useEffect(function() {
        if (dr.deliveryRecord && dr.deliveryRecord.id) {
            nb.setDeliveryRecord(dr.deliveryRecord);
        }
    }, [dr.deliveryRecord]);

    /* ── Delivery edit mode (false = show read-only summary on delivery tab) ── */
    const editDeliveryState = useState(false);
    const editingDelivery = editDeliveryState[0];
    const setEditingDelivery = editDeliveryState[1];

    /* ── Lightweight pair fetch (replaces useMotherBabyPairing) ── */
    const pairsState = useState([]);
    const pairs = pairsState[0];
    const setPairs = pairsState[1];

    const fetchPairs = function() {
        if (!patient_number) return;
        axios.get(config.smsUrl + "/obgyn/mother-baby/" + patient_number)
            .then(function(res) { setPairs(res.data || []); })
            .catch(function() {});
    };

    useEffect(function() {
        fetchPairs();
    }, [patient_number]);

    /* ── Computed pair stats ── */
    const liveBirths = (nb.newborns || []).filter(function(n) { return n.outcome === "alive"; }).length;
    const totalPaired = pairs.length;
    const allPaired = liveBirths > 0 && totalPaired >= liveBirths;

    const closeSnackbar = function() { actions.snackbarActions.hideSnackbar(); };

    const goToAdmission = function() {
        history.push("/gyn-and-obs-admission/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    const goToCSection = function() {
        history.push("/gyn-and-obs-cesarean-section/" + patient_number + "/" + visit_id + "/" + patient_type);
    };

    /* ── Loading ── */
    if (dr.loading) {
        return (
            <div>
                <PageTitle title="Delivery & Birth Record" />
                <SubHeader title="OB/GYN" subTitle="Delivery & Birth Record" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <DRPageWrapper>
                        <DRLoading>Loading delivery data...</DRLoading>
                    </DRPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── No admission ── */
    if (!dr.admission) {
        return (
            <div>
                <PageTitle title="Delivery & Birth Record" />
                <SubHeader title="OB/GYN" subTitle="Delivery & Birth Record" />
                <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                    open={snackbar.open} handleCloseBar={closeSnackbar} />
                <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                    <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                    <DRPageWrapper>
                        <DRNoAdmission>
                            <DRNoAdmissionIcon></DRNoAdmissionIcon>
                            <DRNoAdmissionTitle>No Admission Found</DRNoAdmissionTitle>
                            <DRNoAdmissionDesc>
                                The mother must be admitted before delivery and birth record.
                            </DRNoAdmissionDesc>
                            <DRAdmissionBtn onClick={goToAdmission}>Admit Patient →</DRAdmissionBtn>
                        </DRNoAdmission>
                    </DRPageWrapper>
                </ObGynSidebar>
            </div>
        );
    }

    /* ── Summary View Tab ── */
    const renderSummaryTab = function() {
        if (!dr.deliveryRecord) {
            return (
                <DRNoAdmission>
                    <DRNoAdmissionIcon></DRNoAdmissionIcon>
                    <DRNoAdmissionTitle>No Delivery Record Yet</DRNoAdmissionTitle>
                    <DRNoAdmissionDesc>
                        Record the delivery details in the Delivery Form tab first.
                    </DRNoAdmissionDesc>
                    <DRPrimaryBtn onClick={function() { dr.setActiveTab("delivery"); }}>
                        Go to Delivery Form
                    </DRPrimaryBtn>
                </DRNoAdmission>
            );
        }

        const s = dr.summary;
        const rec = dr.deliveryRecord;

        /* Helper: look up a label from a constant array */
        const labelFor = function(list, value) {
            if (!value) return "—";
            const found = list.find(function(item) { return item.value === value; });
            return found ? found.label : value;
        };

        /* Helper: format complications array to readable labels */
        const complicationLabels = (rec.complications || []).map(function(c) {
            return labelFor(dr.COMPLICATION_OPTIONS, c);
        });

        /* Helper: look up pair for a newborn */
        const pairForNewborn = function(newbornId) {
            return pairs.find(function(p) { return p.newborn_id === newbornId; }) || null;
        };

        /* Helper: format outcome label */
        const outcomeLabel = function(outcome) {
            if (outcome === "alive") return "Live Birth";
            if (outcome === "fresh_stillbirth") return "Fresh Stillbirth";
            if (outcome === "macerated_stillbirth") return "Macerated Stillbirth";
            if (outcome === "neonatal_death") return "Early Neonatal Death";
            return outcome || "—";
        };

        /* Helper: format feeding option */
        const feedingLabel = function(val) {
            if (val === "exclusive_breastfeeding") return "Exclusive Breastfeeding";
            if (val === "replacement_feeding") return "Replacement Feeding";
            if (val === "mixed") return "Mixed Feeding";
            return val || "—";
        };

        /* Helper: cord care label */
        const cordCareLabel = function(val) {
            if (val === "chlorhexidine") return "Chlorhexidine 4%";
            if (val === "dry_care") return "Dry Cord Care";
            return val || "—";
        };

        /* Helper: resuscitation types to labels */
        const resuscLabels = function(types) {
            if (!types || types.length === 0) return "None";
            const map = {
                stimulation: "Stimulation", suction: "Suction",
                bag_mask: "Bag & Mask Ventilation", intubation: "Intubation",
                chest_compressions: "Chest Compressions",
            };
            return types.map(function(t) { return map[t] || t; }).join(", ");
        };

        /* Helper: APGAR score color */
        const apgarColor = function(score) {
            if (score === null || score === undefined) return "#888";
            if (score >= 7) return "#2e7d32";
            if (score >= 4) return "#f57f17";
            return "#c62828";
        };

        /* Section header style for subdivisions within the single delivery card */
        const sectionHead = { margin: "18px 0 8px", fontSize: "0.85rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.5px" };
        const firstSectionHead = { margin: "0 0 8px", fontSize: "0.85rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.5px" };

        return (
            <div>
                {/* ── Delivery Summary (single card) ── */}
                <DRRecordCard>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                        <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#283593" }}>
                            Delivery Summary
                        </h4>
                        <DRSecondaryBtn
                            style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                            onClick={function() { dr.setActiveTab("delivery"); }}
                        >
                            Edit
                        </DRSecondaryBtn>
                    </div>

                    {/* General */}
                    <div style={firstSectionHead}>General</div>
                    <DRRecordRow>
                        <DRRecordLabel>Delivery Date/Time</DRRecordLabel>
                        <DRRecordValue>{s.datetime ? new Date(s.datetime).toLocaleString() : "—"}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Mode of Delivery</DRRecordLabel>
                        <DRRecordValue>{s.mode}</DRRecordValue>
                    </DRRecordRow>
                    {rec.indication_operative ? (
                        <DRRecordRow>
                            <DRRecordLabel>Indication (Operative)</DRRecordLabel>
                            <DRRecordValue>{rec.indication_operative}</DRRecordValue>
                        </DRRecordRow>
                    ) : null}
                    <DRRecordRow>
                        <DRRecordLabel>Number of Babies</DRRecordLabel>
                        <DRRecordValue>{s.babies}</DRRecordValue>
                    </DRRecordRow>

                    {/* Labour Stages */}
                    <div style={sectionHead}>Labour Stages</div>
                    <DRRecordRow>
                        <DRRecordLabel>1st Stage Duration</DRRecordLabel>
                        <DRRecordValue>{rec.duration_first_stage_hours ? rec.duration_first_stage_hours + " hours" : "—"}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>2nd Stage Duration</DRRecordLabel>
                        <DRRecordValue>{rec.duration_second_stage_minutes ? rec.duration_second_stage_minutes + " minutes" : "—"}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>3rd Stage Duration</DRRecordLabel>
                        <DRRecordValue>{rec.duration_third_stage_minutes ? rec.duration_third_stage_minutes + " minutes" : "—"}</DRRecordValue>
                    </DRRecordRow>

                    {/* Third Stage Management */}
                    <div style={sectionHead}>Third Stage Management</div>
                    <DRRecordRow>
                        <DRRecordLabel>Placenta Complete</DRRecordLabel>
                        <DRRecordValue warn={rec.placenta_complete === false}>
                            {rec.placenta_complete ? "Yes" : "No"}
                        </DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Placenta Delivery Method</DRRecordLabel>
                        <DRRecordValue>{labelFor(dr.PLACENTA_METHODS, rec.placenta_delivery_method)}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Uterotonic Given</DRRecordLabel>
                        <DRRecordValue>{labelFor(dr.UTEROTONICS, rec.uterotonic_given)}</DRRecordValue>
                    </DRRecordRow>
                    {rec.uterotonic_time ? (
                        <DRRecordRow>
                            <DRRecordLabel>Uterotonic Time</DRRecordLabel>
                            <DRRecordValue>{new Date(rec.uterotonic_time).toLocaleString()}</DRRecordValue>
                        </DRRecordRow>
                    ) : null}
                    <DRRecordRow>
                        <DRRecordLabel>Cord Clamping</DRRecordLabel>
                        <DRRecordValue>{labelFor(dr.CORD_CLAMPING, rec.cord_clamping)}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Estimated Blood Loss</DRRecordLabel>
                        <DRRecordValue warn={parseFloat(s.bloodLoss) >= 500}>
                            {s.bloodLoss} ml
                        </DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>AMTSL</DRRecordLabel>
                        <DRRecordValue>{s.amtsl}</DRRecordValue>
                    </DRRecordRow>

                    {/* Perineal Status */}
                    <div style={sectionHead}>Perineal Status</div>
                    <DRRecordRow>
                        <DRRecordLabel>Episiotomy</DRRecordLabel>
                        <DRRecordValue>{labelFor(dr.EPISIOTOMY_TYPES, rec.episiotomy)}</DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Perineal Tear</DRRecordLabel>
                        <DRRecordValue warn={rec.perineal_tear_degree && rec.perineal_tear_degree !== "none"}>
                            {labelFor(dr.TEAR_DEGREES, rec.perineal_tear_degree)}
                        </DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Perineal Repair</DRRecordLabel>
                        <DRRecordValue>{rec.perineal_repair ? "Yes" : "No"}</DRRecordValue>
                    </DRRecordRow>

                    {/* Maternal Condition */}
                    <div style={sectionHead}>Maternal Condition</div>
                    <DRRecordRow>
                        <DRRecordLabel>Post-Delivery Condition</DRRecordLabel>
                        <DRRecordValue warn={s.condition === "critical" || s.condition === "unstable"}>
                            {labelFor(dr.MATERNAL_CONDITIONS, rec.maternal_condition_post_delivery)}
                        </DRRecordValue>
                    </DRRecordRow>
                    <DRRecordRow>
                        <DRRecordLabel>Complications</DRRecordLabel>
                        <DRRecordValue warn={complicationLabels.length > 0 && complicationLabels[0] !== "No Complications"}>
                            {complicationLabels.length > 0 ? complicationLabels.join(", ") : "None"}
                        </DRRecordValue>
                    </DRRecordRow>
                    {rec.notes ? (
                        <DRRecordRow>
                            <DRRecordLabel>Notes</DRRecordLabel>
                            <DRRecordValue>{rec.notes}</DRRecordValue>
                        </DRRecordRow>
                    ) : null}
                </DRRecordCard>

                {/* ── Newborn Records ── */}
                <DRRecordCard>
                    <h4 style={{ margin: "0 0 16px", fontSize: "1rem", fontWeight: 700, color: "#2e7d32" }}>
                        Newborn Records — {nb.newborns.length} {nb.newborns.length === 1 ? "Baby" : "Babies"}
                    </h4>
                    {nb.newborns.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "20px 0", color: "#888", fontSize: "0.85rem" }}>
                            No newborn records yet. Use the Newborn tab to add an assessment.
                        </div>
                    ) : (
                        nb.newborns.map(function(baby, idx) {
                            const babyPair = pairForNewborn(baby.id);
                            return (
                                <div key={baby.id || idx} style={{
                                    padding: "16px", borderRadius: "8px",
                                    background: "#fff",
                                    border: "1px solid #e0e0e0", marginBottom: idx < nb.newborns.length - 1 ? "12px" : "0",
                                }}>
                                    {/* Header row */}
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", paddingBottom: "8px", borderBottom: "1px solid #e0e0e0" }}>
                                        <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#283593" }}>
                                            Baby #{baby.birth_order || (idx + 1)} — {baby.sex === "male" ? "Male" : baby.sex === "female" ? "Female" : "—"}
                                            {babyPair ? (
                                                <span style={{ marginLeft: "10px", fontSize: "0.8rem", color: "#1565c0", fontWeight: 600 }}>
                                                    (Patient #{babyPair.baby_patient_number})
                                                </span>
                                            ) : null}
                                        </div>
                                        <DRSecondaryBtn
                                            style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                                            onClick={function() { nb.startEditNewborn(baby); dr.setActiveTab("newborn"); }}
                                        >
                                            Edit
                                        </DRSecondaryBtn>
                                    </div>

                                    {/* Outcome & Measurements */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        Outcome & Measurements
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555", marginBottom: "12px" }}>
                                        <span>Outcome: <strong style={{ color: baby.outcome === "alive" ? "#2e7d32" : "#c62828" }}>
                                            {outcomeLabel(baby.outcome)}
                                        </strong></span>
                                        <span>Weight: <strong>{baby.birth_weight_grams ? baby.birth_weight_grams + "g" : "—"}</strong></span>
                                        <span>Length: <strong>{baby.birth_length_cm ? baby.birth_length_cm + " cm" : "—"}</strong></span>
                                        <span>Head Circ.: <strong>{baby.head_circumference_cm ? baby.head_circumference_cm + " cm" : "—"}</strong></span>
                                    </div>

                                    {/* APGAR Scores */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        APGAR Scores
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555", marginBottom: "12px" }}>
                                        <span>1 min: <strong style={{ color: apgarColor(baby.apgar_1min) }}>
                                            {baby.apgar_1min !== null && baby.apgar_1min !== undefined ? baby.apgar_1min + "/10" : "—"}
                                        </strong></span>
                                        <span>5 min: <strong style={{ color: apgarColor(baby.apgar_5min) }}>
                                            {baby.apgar_5min !== null && baby.apgar_5min !== undefined ? baby.apgar_5min + "/10" : "—"}
                                        </strong></span>
                                        <span>10 min: <strong style={{ color: apgarColor(baby.apgar_10min) }}>
                                            {baby.apgar_10min !== null && baby.apgar_10min !== undefined ? baby.apgar_10min + "/10" : "—"}
                                        </strong></span>
                                    </div>

                                    {/* Resuscitation */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        Resuscitation
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555", marginBottom: "12px" }}>
                                        <span>Needed: <strong style={{ color: baby.resuscitation_needed ? "#c62828" : "#2e7d32" }}>
                                            {baby.resuscitation_needed ? "Yes" : "No"}
                                        </strong></span>
                                        {baby.resuscitation_needed ? (
                                            <span>Type: <strong>{resuscLabels(baby.resuscitation_type)}</strong></span>
                                        ) : null}
                                    </div>

                                    {/* Immediate Care */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        Immediate Care
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555", marginBottom: "12px" }}>
                                        <span>Skin-to-Skin: <strong>{baby.skin_to_skin_initiated ? "Yes" : "No"}</strong></span>
                                        <span>Breastfed within 1hr: <strong>{baby.breastfeeding_within_1hr ? "Yes" : "No"}</strong></span>
                                        <span>Vitamin K: <strong>{baby.vitamin_k_given ? "Yes" : "No"}</strong></span>
                                        <span>Eye Prophylaxis: <strong>{baby.eye_prophylaxis_given ? "Yes" : "No"}</strong></span>
                                        <span>Cord Care: <strong>{cordCareLabel(baby.cord_care)}</strong></span>
                                    </div>

                                    {/* Vaccinations */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        Vaccinations
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555", marginBottom: "12px" }}>
                                        <span>BCG: <strong>{baby.bcg_given ? "Given" : "No"}</strong></span>
                                        <span>OPV-0: <strong>{baby.opv0_given ? "Given" : "No"}</strong></span>
                                        <span>Hep B: <strong>{baby.hep_b_birth_dose ? "Given" : "No"}</strong></span>
                                        <span>Nevirapine: <strong>{baby.nevirapine_given ? "Given" : "No"}</strong></span>
                                    </div>

                                    {/* Feeding & Anomalies */}
                                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
                                        Feeding & Other
                                    </div>
                                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", fontSize: "0.82rem", color: "#555" }}>
                                        <span>Feeding: <strong>{feedingLabel(baby.feeding_option)}</strong></span>
                                        {baby.congenital_anomalies ? (
                                            <span>Congenital Anomalies: <strong style={{ color: "#c62828" }}>{baby.congenital_anomalies}</strong></span>
                                        ) : null}
                                    </div>

                                    {/* Physical Exam Notes */}
                                    {baby.physical_exam_notes ? (
                                        <div style={{ marginTop: "8px", fontSize: "0.82rem", color: "#555" }}>
                                            <span>Physical Exam: <strong>{baby.physical_exam_notes}</strong></span>
                                        </div>
                                    ) : null}

                                    {/* Pair info */}
                                    {babyPair ? (
                                        <div style={{ marginTop: "12px", padding: "8px 12px", background: "#e3f2fd", borderRadius: "6px", fontSize: "0.8rem", color: "#1565c0" }}>
                                            Paired — Patient #{babyPair.baby_patient_number} | {babyPair.baby_name || "—"} | Status: {babyPair.pair_status || "active"}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })
                    )}
                </DRRecordCard>

            </div>
        );
    };

    /* ── Delivery Summary Card (shown on delivery tab when record exists and not editing) ── */
    const renderDeliverySummaryCard = function() {
        const s = dr.summary;
        const rec = dr.deliveryRecord;

        const labelFor = function(list, value) {
            if (!value) return "—";
            const found = list.find(function(item) { return item.value === value; });
            return found ? found.label : value;
        };

        const complicationLabels = (rec.complications || []).map(function(c) {
            return labelFor(dr.COMPLICATION_OPTIONS, c);
        });

        const sectionHead = { margin: "18px 0 8px", fontSize: "0.85rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.5px" };
        const firstSectionHead = { margin: "0 0 8px", fontSize: "0.85rem", fontWeight: 700, color: "#546e7a", textTransform: "uppercase", letterSpacing: "0.5px" };

        return (
            <DRRecordCard>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h4 style={{ margin: 0, fontSize: "1rem", fontWeight: 700, color: "#283593" }}>
                        Delivery Summary
                    </h4>
                    <DRSecondaryBtn
                        style={{ padding: "3px 10px", fontSize: "0.75rem" }}
                        onClick={function() { setEditingDelivery(true); }}
                    >
                        Edit
                    </DRSecondaryBtn>
                </div>

                <div style={firstSectionHead}>General</div>
                <DRRecordRow>
                    <DRRecordLabel>Delivery Date/Time</DRRecordLabel>
                    <DRRecordValue>{s.datetime ? new Date(s.datetime).toLocaleString() : "—"}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Mode of Delivery</DRRecordLabel>
                    <DRRecordValue>{s.mode}</DRRecordValue>
                </DRRecordRow>
                {rec.indication_operative ? (
                    <DRRecordRow>
                        <DRRecordLabel>Indication (Operative)</DRRecordLabel>
                        <DRRecordValue>{rec.indication_operative}</DRRecordValue>
                    </DRRecordRow>
                ) : null}
                <DRRecordRow>
                    <DRRecordLabel>Number of Babies</DRRecordLabel>
                    <DRRecordValue>{s.babies}</DRRecordValue>
                </DRRecordRow>

                <div style={sectionHead}>Labour Stages</div>
                <DRRecordRow>
                    <DRRecordLabel>1st Stage Duration</DRRecordLabel>
                    <DRRecordValue>{rec.duration_first_stage_hours ? rec.duration_first_stage_hours + " hours" : "—"}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>2nd Stage Duration</DRRecordLabel>
                    <DRRecordValue>{rec.duration_second_stage_minutes ? rec.duration_second_stage_minutes + " minutes" : "—"}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>3rd Stage Duration</DRRecordLabel>
                    <DRRecordValue>{rec.duration_third_stage_minutes ? rec.duration_third_stage_minutes + " minutes" : "—"}</DRRecordValue>
                </DRRecordRow>

                <div style={sectionHead}>Third Stage Management</div>
                <DRRecordRow>
                    <DRRecordLabel>Placenta Complete</DRRecordLabel>
                    <DRRecordValue warn={rec.placenta_complete === false}>
                        {rec.placenta_complete ? "Yes" : "No"}
                    </DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Placenta Delivery Method</DRRecordLabel>
                    <DRRecordValue>{labelFor(dr.PLACENTA_METHODS, rec.placenta_delivery_method)}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Uterotonic Given</DRRecordLabel>
                    <DRRecordValue>{labelFor(dr.UTEROTONICS, rec.uterotonic_given)}</DRRecordValue>
                </DRRecordRow>
                {rec.uterotonic_time ? (
                    <DRRecordRow>
                        <DRRecordLabel>Uterotonic Time</DRRecordLabel>
                        <DRRecordValue>{new Date(rec.uterotonic_time).toLocaleString()}</DRRecordValue>
                    </DRRecordRow>
                ) : null}
                <DRRecordRow>
                    <DRRecordLabel>Cord Clamping</DRRecordLabel>
                    <DRRecordValue>{labelFor(dr.CORD_CLAMPING, rec.cord_clamping)}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Estimated Blood Loss</DRRecordLabel>
                    <DRRecordValue warn={parseFloat(s.bloodLoss) >= 500}>
                        {s.bloodLoss} ml
                    </DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>AMTSL</DRRecordLabel>
                    <DRRecordValue>{s.amtsl}</DRRecordValue>
                </DRRecordRow>

                <div style={sectionHead}>Perineal Status</div>
                <DRRecordRow>
                    <DRRecordLabel>Episiotomy</DRRecordLabel>
                    <DRRecordValue>{labelFor(dr.EPISIOTOMY_TYPES, rec.episiotomy)}</DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Perineal Tear</DRRecordLabel>
                    <DRRecordValue warn={rec.perineal_tear_degree && rec.perineal_tear_degree !== "none"}>
                        {labelFor(dr.TEAR_DEGREES, rec.perineal_tear_degree)}
                    </DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Perineal Repair</DRRecordLabel>
                    <DRRecordValue>{rec.perineal_repair ? "Yes" : "No"}</DRRecordValue>
                </DRRecordRow>

                <div style={sectionHead}>Maternal Condition</div>
                <DRRecordRow>
                    <DRRecordLabel>Post-Delivery Condition</DRRecordLabel>
                    <DRRecordValue warn={s.condition === "critical" || s.condition === "unstable"}>
                        {labelFor(dr.MATERNAL_CONDITIONS, rec.maternal_condition_post_delivery)}
                    </DRRecordValue>
                </DRRecordRow>
                <DRRecordRow>
                    <DRRecordLabel>Complications</DRRecordLabel>
                    <DRRecordValue warn={complicationLabels.length > 0 && complicationLabels[0] !== "No Complications"}>
                        {complicationLabels.length > 0 ? complicationLabels.join(", ") : "None"}
                    </DRRecordValue>
                </DRRecordRow>
                {rec.notes ? (
                    <DRRecordRow>
                        <DRRecordLabel>Notes</DRRecordLabel>
                        <DRRecordValue>{rec.notes}</DRRecordValue>
                    </DRRecordRow>
                ) : null}
            </DRRecordCard>
        );
    };

    /* ── Main Render ── */
    return (
        <div>
            <PageTitle title="Delivery & Birth Record" />
            <SubHeader title="OB/GYN" subTitle="Delivery & Birth Record" />
            <MainSnackbar variant={snackbar.variant} message={snackbar.message}
                open={snackbar.open} handleCloseBar={closeSnackbar} />
            <ObGynSidebar patient_number={patient_number} visit_id={visit_id} patient_type={patient_type}>
                <ObGynPatientInfo patient_number={patient_number} visit_id={visit_id} patient_type={patient_type} />
                <DRPageWrapper>

                    {/* Summary strip (if delivery recorded) */}
                    {dr.summary ? (
                        <DRSummaryStrip>
                            <DRSummaryItem>
                                <DRSummaryLabel>Mode</DRSummaryLabel>
                                <DRSummaryValue>{dr.form.delivery_mode ? dr.form.delivery_mode.toUpperCase().replace("_", " ") : "—"}</DRSummaryValue>
                            </DRSummaryItem>
                            <DRSummaryItem warn={parseFloat(dr.summary.bloodLoss) >= 500}>
                                <DRSummaryLabel>Blood Loss</DRSummaryLabel>
                                <DRSummaryValue>{dr.summary.bloodLoss} ml</DRSummaryValue>
                            </DRSummaryItem>
                            <DRSummaryItem>
                                <DRSummaryLabel>Babies</DRSummaryLabel>
                                <DRSummaryValue>{dr.summary.babies}</DRSummaryValue>
                            </DRSummaryItem>
                            <DRSummaryItem>
                                <DRSummaryLabel>Newborns</DRSummaryLabel>
                                <DRSummaryValue>{nb.newborns.length} recorded</DRSummaryValue>
                            </DRSummaryItem>
                            <DRSummaryItem>
                                <DRSummaryLabel>Paired</DRSummaryLabel>
                                <DRSummaryValue>{allPaired ? "All" : totalPaired + "/" + liveBirths}</DRSummaryValue>
                            </DRSummaryItem>
                            <DRSummaryItem warn={dr.summary.condition !== "stable"}>
                                <DRSummaryLabel>Condition</DRSummaryLabel>
                                <DRSummaryValue>{dr.summary.condition}</DRSummaryValue>
                            </DRSummaryItem>
                        </DRSummaryStrip>
                    ) : null}

                    {/* Alerts (show on delivery tab) */}
                    {/* {dr.alerts.length > 0 && dr.activeTab === "delivery" ? (
                        <DRAlertContainer>
                            {dr.alerts.map(function(alert, idx) {
                                return (
                                    <DRAlertCard key={idx} severity={alert.type}>
                                        <DRAlertIcon>{alert.type === "critical" ? "" : alert.type === "warning" ? "" : ""}</DRAlertIcon>
                                        <DRAlertContent>
                                            <DRAlertTitle>{alert.title}</DRAlertTitle>
                                            <DRAlertDetail>{alert.detail}</DRAlertDetail>
                                            <DRAlertAction>{alert.action}</DRAlertAction>
                                        </DRAlertContent>
                                    </DRAlertCard>
                                );
                            })}
                        </DRAlertContainer>
                    ) : null} */}

                    {/* Tabs */}
                    <ObGynTabBar>
                        <ObGynTabBtn active={dr.activeTab === "delivery"}
                            onClick={function() { setEditingDelivery(false); dr.setActiveTab("delivery"); }}>
                            Delivery
                        </ObGynTabBtn>
                        <ObGynTabBtn active={dr.activeTab === "newborn"}
                            onClick={function() { setEditingDelivery(false); dr.setActiveTab("newborn"); }}>
                            Newborn ({nb.newborns.length})
                        </ObGynTabBtn>
                        <ObGynTabBtn active={dr.activeTab === "summary"}
                            onClick={function() { setEditingDelivery(false); dr.setActiveTab("summary"); }}>
                            Summary
                        </ObGynTabBtn>
                    </ObGynTabBar>

                    {/* Tab content */}
                    {dr.activeTab === "delivery" ? (
                        dr.deliveryRecord && !editingDelivery
                            ? renderDeliverySummaryCard()
                            : <DeliveryRecordForm dr={dr} doctorName={doctor_name} />
                    ) : null}
                    {dr.activeTab === "newborn" ? (
                        <NewbornAssessmentTab nb={nb} deliveryRecord={dr.deliveryRecord} />
                    ) : null}
                    {dr.activeTab === "summary" ? renderSummaryTab() : null}
                </DRPageWrapper>
            </ObGynSidebar>
        </div>
    );
};

/* ─── Redux ─── */
function mapStateToProps(state) {
    return { snackbar: state.snackbar };
}

function mapDispatchToProps(dispatch) {
    return { actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) } };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryRecordPage);
